/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { PanelFocusLock } from '../panel-focus-lock/panel-focus-lock';

export interface PanelContainerProps {
	/**
	 * The content of the panel container.
	 */
	children: ReactNode;
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * When `true`, a focus trap is activated so that Tab / Shift+Tab cycles only
	 * through interactive elements inside the panel.
	 *
	 * Useful for panels that require the user to complete an action before continuing.
	 *
	 * @default false
	 */
	isFocusLockEnabled?: boolean;
}

const styles = cssMap({
	wrapper: {
		height: '100%',
		backgroundColor: token('elevation.surface'),
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		flex: '1 1 0%',
		minHeight: '0px',
		overflow: 'hidden',
	},
});

/**
 * The PanelContainer component provides a standardized container for displaying content
 * in a side panel layout. It follows the design system patterns and integrates
 * with the navigation system for consistent styling and behavior.
 *
 * Compose it with a fixed `PanelHeader` / `PanelFooter` and a scrollable `PanelContent`
 * (wrapping `PanelSubheader` and `PanelBody`).
 *
 * For resizable panels, use the `PanelSplitter` from `@atlaskit/navigation-system/layout/panel-splitter`
 * alongside the `Panel` slot from `@atlaskit/navigation-system/layout/panel`.
 */
export function PanelContainer({
	children,
	testId,
	isFocusLockEnabled = false,
}: PanelContainerProps): JSX.Element {
	return (
		<PanelFocusLock isFocusLockEnabled={isFocusLockEnabled}>
			<Box as="section" testId={testId} xcss={styles.wrapper}>
				<Box xcss={styles.content}>{children}</Box>
			</Box>
		</PanelFocusLock>
	);
}
